import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// react-router-dom v5 -> v7 compatibility shims.
// v7 removed `useHistory` and `withRouter`; these thin wrappers reproduce the
// v5 surface (a `history`-like object + injected router props) on top of the
// v7 hooks so existing call sites keep working with only an import change.

type AnyTo = string | number | { pathname?: string; search?: string };

function makeHistory(navigate: ReturnType<typeof useNavigate>, location: ReturnType<typeof useLocation>) {
  return {
    push: (to: AnyTo) => navigate(to as never),
    replace: (to: AnyTo) => navigate(to as never, { replace: true }),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    go: (n: number) => navigate(n),
    location,
  };
}

export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  return makeHistory(navigate, location);
}

export function withRouter<P extends object = Record<string, unknown>, _C = unknown>(
  Component: React.ComponentType<any>
) {
  return function WithRouter(props: P) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const history = makeHistory(navigate, location);
    return (
      <Component
        {...(props as P)}
        history={history}
        location={location}
        match={{ params }}
        navigate={navigate}
      />
    );
  };
}
